import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import {
  useLocalParticipant,
  useSessionMessages,
} from "@livekit/components-react";
import { cardsSignal, type ReceivedToolResult } from "../../useToolResults";
import { useConversationTimeline } from "../useConversationTimeline";
import { StarterChips } from "../StarterChips/StarterChips";

export const STICK_THRESHOLD_PX = 60;

// Should the list stay pinned to the bottom after a scroll event?
// Near the bottom → stuck. Otherwise only UNSTICK when the user actually moved
// UP from where we last pinned. This matters because a scroll event says nothing
// about who caused it: when a card grows (skeleton → full height) the content
// below the viewport gets taller, distance-from-bottom jumps, and the scroll
// event the browser queues from our own last pin lands AFTER that growth. Read
// naively that looks identical to "user scrolled away", so the list would
// unstick and freeze mid-conversation with the new card half off-screen.
// scrollTop is the tell — content growing below never moves it.
// A scroll is the user's only within this long after their own input (wheel,
// touch drag, keys, scrollbar press). Everything else (our pins, and the browser
// clamping scrollTop when content above shrinks, e.g. a skeleton swapping for a
// shorter card) must never unstick the list: that is how it used to stop
// following mid-conversation and leave new cards or the survey half off-screen.
export const USER_SCROLL_WINDOW_MS = 400;

export function isUserScroll(lastUserInputAt: number, now: number): boolean {
  return now - lastUserInputAt < USER_SCROLL_WINDOW_MS;
}

export function nextStick({
  scrollTop,
  scrollHeight,
  clientHeight,
  lastPinnedTop,
  wasStuck,
}: {
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
  lastPinnedTop: number;
  wasStuck: boolean;
}): boolean {
  if (scrollHeight - scrollTop - clientHeight < STICK_THRESHOLD_PX) return true;
  // 4px of slack absorbs sub-pixel/rounding jitter in scrollTop.
  if (scrollTop < lastPinnedTop - 4) return false;
  return wasStuck;
}

export function MessageList({
  messages,
  connecting,
  userInitials,
  avatarUrl,
  toolResults,
  agentState,
  initialLoading,
  footer,
  starterChips,
  pendingMessages,
  localGreeting,
  greetingDisplay,
}: {
  messages: ReturnType<typeof useSessionMessages>["messages"];
  connecting: boolean;
  userInitials?: string | null;
  avatarUrl?: string;
  toolResults: ReceivedToolResult[];
  agentState?: string;
  initialLoading?: boolean;
  footer?: ReactNode;
  // Quick-start chips, rendered after the typing indicator / greeting. Omit to
  // hide them.
  starterChips?: { onPick: (message: string) => void };
  // Lines the user typed (or tapped) before the agent was connected. Shown as
  // normal user bubbles until they are actually sent.
  pendingMessages?: string[];
  // Courtney's greeting, shown by the widget until hers arrives (greeting.ts).
  localGreeting?: string | null;
  // Greeting animation frame text; see useConversationTimeline.
  greetingDisplay?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const stickRef = useRef(true);
  const { localParticipant } = useLocalParticipant();
  const items = useConversationTimeline({
    messages,
    toolResults,
    localIdentity: localParticipant.identity,
    userInitials,
    avatarUrl,
    localGreeting,
    greetingDisplay,
  });

  // Where we last put the scrollbar ourselves — the baseline nextStick compares
  // against to tell a real user scroll apart from content growing underneath.
  const lastPinnedTopRef = useRef(0);

  const pinToBottom = () => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    lastPinnedTopRef.current = el.scrollTop;
  };

  const userInputAtRef = useRef(0);
  const markUserInput = () => {
    userInputAtRef.current = Date.now();
  };
  const onScroll = () => {
    const el = containerRef.current;
    if (!el) return;
    if (!isUserScroll(userInputAtRef.current, Date.now())) {
      // Not the user: never unstick. Reaching the bottom still re-sticks.
      if (el.scrollHeight - el.scrollTop - el.clientHeight < STICK_THRESHOLD_PX) {
        stickRef.current = true;
      }
      return;
    }
    stickRef.current = nextStick({
      scrollTop: el.scrollTop,
      scrollHeight: el.scrollHeight,
      clientHeight: el.clientHeight,
      lastPinnedTop: lastPinnedTopRef.current,
      wasStuck: stickRef.current,
    });
  };

  // Scroll-to-dismiss the soft keyboard — directional. Dragging the conversation
  // toward older messages (finger moving DOWN from where the drag began) blurs
  // the composer so the keyboard slides away; dragging toward the latest message
  // leaves the keyboard up. iPhones have no system "hide keyboard" button, so
  // this mirrors the native keyboardDismissMode="on-drag" feel. We key off
  // touchmove (a real finger drag), not the scroll event, so the programmatic
  // stick-to-bottom on new messages never dismisses it. getRootNode()
  // .activeElement reaches the focused node inside the widget's shadow root (and
  // the /mobile embed's plain document).
  const touchStartYRef = useRef(0);
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartYRef.current = e.touches[0]?.clientY ?? 0;
    markUserInput();
  };
  const dismissKeyboardOnDrag = (e: React.TouchEvent) => {
    markUserInput();
    const dy = (e.touches[0]?.clientY ?? 0) - touchStartYRef.current;
    if (dy <= 8) return; // only when dragging down = scrolling up to history
    const root = containerRef.current?.getRootNode() as
      | ShadowRoot
      | Document
      | null;
    const active = root?.activeElement as HTMLElement | null;
    if (active && (active.tagName === "TEXTAREA" || active.tagName === "INPUT")) {
      active.blur();
    }
  };

  // Lift-to-keyboard: when the soft keyboard opens/closes the list shrinks/grows
  // to fit the remaining space. If we were pinned to the bottom, re-pin so the
  // latest message stays glued just above the keyboard (the native
  // "conversation lifts up" feel) instead of sliding out of view. If the user is
  // scrolled up reading history (not stuck), leave their position untouched.
  // Watch the container itself rather than visualViewport: the /mobile embed
  // defers its viewport pin past the iOS keyboard animation, so the viewport
  // event fires ~350ms before the list actually resizes — this fires when the
  // resize lands, whatever caused it.
  // The footer is watched the same way: it grows as the user interacts with it
  // (e.g. feedback chips appear), which changes no message and so wouldn't
  // otherwise re-pin.
  const footerRef = useRef<HTMLDivElement>(null);
  const hasFooter = !!footer;
  // Every row is watched too (see the effect below): a card that grows after it
  // mounts (results morphing in over the skeleton, the carousel laying out) or
  // the survey's follow-up chips change no message, so nothing else re-pins.
  // Pinned inside the callback, which runs after layout and before paint, so the
  // gap is never drawn; a requestAnimationFrame here showed a jump of up to
  // ~180 px for a frame.
  const roRef = useRef<ResizeObserver | null>(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      if (stickRef.current) pinToBottom();
    });
    ro.observe(el);
    if (footerRef.current) ro.observe(footerRef.current);
    roRef.current = ro;
    return () => {
      ro.disconnect();
      roRef.current = null;
    };
  }, [hasFooter]);
  // Layout effects (before paint), so a newly mounted row (thinking dots, a
  // card, the survey) is never drawn below the fold for a frame first.
  useLayoutEffect(() => {
    const el = containerRef.current;
    const ro = roRef.current;
    if (!el || !ro) return;
    for (const row of Array.from(el.children)) ro.observe(row);
  });

  // Re-scroll on new messages AND while the last message keeps streaming in.
  // Without the trailing length, the effect never re-fires while the agent's
  // current reply is still growing — bottom drops out of view mid-response.
  // "Courtney is thinking" bubble while the LLM works — covers the gap before
  // any reply/skeleton appears. Suppressed when a skeleton is already showing
  // (the search itself signals activity), to avoid a double indicator.
  const hasSkeleton = toolResults.some(
    (r) => (r.data as Record<string, unknown>)?.loading === true,
  );
  const lastMsg = messages[messages.length - 1];
  // We're awaiting the agent's reply when the LAST message is the caller's.
  const lastIsUser =
    !!lastMsg &&
    (lastMsg.type === "userTranscript" ||
      lastMsg.from?.identity === localParticipant.identity);
  const awaitingReply = lastIsUser && !hasSkeleton;
  // Show "thinking" only once the caller's turn has ENDED and the agent is
  // actually processing (agentState === "thinking") — NOT while they're still
  // talking (the partial transcript lands mid-sentence, which felt like an
  // interruption). Once shown it LATCHES on through the thinking→speaking
  // transition (we simply don't turn it off) until the reply lands, so the
  // avatar never unmounts into a gap. Hides the instant the reply/skeleton shows.
  const [showThinking, setShowThinking] = useState(false);
  useEffect(() => {
    // Clear the moment we're no longer awaiting a reply OR the agent is gone —
    // ending the call mid-"thinking" leaves agentState "disconnected" (or
    // undefined) while the last message is still the caller's, which would
    // otherwise keep the bubble latched on forever after the call ends.
    if (!awaitingReply || !agentState || agentState === "disconnected") {
      setShowThinking(false);
      return;
    }
    if (agentState === "thinking") {
      const t = setTimeout(() => setShowThinking(true), 200);
      return () => clearTimeout(t);
    }
    // Past "thinking" but the reply isn't in yet → keep current state (latch).
  }, [awaitingReply, agentState]);

  const scrollSignal = `${messages.length}:${cardsSignal(toolResults)}:${lastMsg?.id ?? ""}:${lastMsg?.message?.length ?? 0}:${showThinking}:${pendingMessages?.length ?? 0}`;
  // Pin to the bottom by setting scrollTop directly (instant) rather than
  // scrollIntoView — the latter animates and, fired on every streamed token,
  // produces the constant "smacking"/yank. Direct scrollTop just keeps the
  // bottom glued as content grows, so it reads as smooth growth.
  useLayoutEffect(() => {
    if (!stickRef.current) return;
    pinToBottom();
  }, [scrollSignal]);

  useEffect(() => {
    requestAnimationFrame(pinToBottom);
  }, []);

  return (
    <div
      className="lk-widget-messages"
      ref={containerRef}
      onScroll={onScroll}
      onWheel={markUserInput}
      onKeyDown={markUserInput}
      onPointerDown={markUserInput}
      onPointerMove={(e) => e.buttons && markUserInput()}
      onTouchStart={onTouchStart}
      onTouchMove={dismissKeyboardOnDrag}
    >
      {items.map((it) => it.node)}
      {pendingMessages?.map((text, i) => (
        <div key={`pending-${i}`} className="lk-widget-msg lk-widget-msg--user">
          <div className="lk-widget-msg-row">
            <div className="lk-widget-msg-bubble">{text}</div>
            {userInitials && (
              <span className="lk-widget-msg-initials" aria-hidden="true">
                {userInitials}
              </span>
            )}
          </div>
        </div>
      ))}
      {/* Thinking dots: mid-conversation while awaiting a reply, AND the whole
          initial window (connect → room made → greeting) so it doesn't vanish
          when the socket connects but before she's spoken. */}
      {(showThinking || initialLoading) && (
        <div className="lk-widget-msg lk-widget-msg--agent">
          <div className="lk-widget-msg-agent-row">
            {avatarUrl && (
              <div
                className="lk-widget-msg-agent-avatar"
                style={{ backgroundImage: `url(${avatarUrl})` }}
                role="img"
                aria-label="Courtney"
              />
            )}
            <div className="lk-widget-msg-agent-bubble lk-widget-thinking" aria-label="Courtney is thinking">
              <span className="lk-thinking-dot" />
              <span className="lk-thinking-dot" />
              <span className="lk-thinking-dot" />
            </div>
          </div>
        </div>
      )}
      {starterChips && <StarterChips avatarUrl={avatarUrl} onPick={starterChips.onPick} />}
      {footer && <div ref={footerRef}>{footer}</div>}
      <div ref={endRef} />
    </div>
  );
}
